import { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Space, Typography, Button, message, Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { formatVND, moneyFormatter, moneyParser } from '@/utils/format';
import { b2bRebateLedgerService } from '@/services/b2b-rebate-ledger.service';
import { b2bRebateLedgerKeys } from '@/services/queryKeys';

const { Text } = Typography;

const CAP_EXHAUSTED_MESSAGE =
  'Tiền hàng còn lại sau voucher/điểm đã hết, không thể áp chiết khấu tích luỹ';

export interface TradeDiscountResult {
  amount: number;
  note?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Tổng thanh toán trước CK này (sau CK dòng + VAT). */
  orderTotal: number;
  /** Trần theo tiền hàng còn lại sau voucher + điểm; thiếu thì dùng orderTotal. */
  tradeCap?: number;
  /** Số khách phải trả thật theo pipeline POS khi CK = a; thiếu thì orderTotal - a. */
  previewTradePayable?: (amount: number) => number;
  initialAmount?: number;
  /** customer_user_id của KH tổ chức — BE resolve sang customer B2B. */
  customerId?: string;
  /** Đơn đang sửa — BE loại khoản hold của chính đơn khỏi hạn mức đã sử dụng. */
  orderId?: string;
  onConfirm: (result: TradeDiscountResult) => void;
}

/**
 * Modal nhập chiết khấu tích luỹ (số tiền) trừ thẳng vào tổng đơn B2B.
 * Số dư = CK đã quyết toán − đã trừ (COMPLETED) − đang giữ trên đơn mở.
 */
export function TradeDiscountModal({
  open,
  onClose,
  orderTotal,
  tradeCap,
  previewTradePayable,
  initialAmount = 0,
  customerId,
  orderId,
  onConfirm,
}: Props) {
  const [amount, setAmount] = useState<number | null>(initialAmount || null);

  const { data: credit, isFetching } = useQuery({
    queryKey: customerId
      ? [...b2bRebateLedgerKeys.all, 'credit', customerId, orderId || 'new-order']
      : [...b2bRebateLedgerKeys.all, 'credit-empty'],
    queryFn: () => b2bRebateLedgerService.credit(customerId || '', orderId),
    enabled: open && Boolean(customerId),
    staleTime: 15_000,
  });

  const settled = Math.round(Number(credit?.settled_amount || 0));
  const used = Math.round(Number(credit?.applied_amount || 0));
  const reserved = Math.round(Number(credit?.reserved_amount || 0));
  const currentOrderReserved = Math.round(
    Number(credit?.current_order_reserved_amount || 0),
  );
  const otherReserved = Math.max(0, reserved - currentOrderReserved);
  const remaining = Math.round(Number(credit?.available_amount || 0));
  // Trần CK: tiền hàng còn lại (pipeline) nếu có, không thì tổng đơn cũ.
  const capBase = tradeCap ?? orderTotal;
  const payableAfter = (a: number) =>
    previewTradePayable ? previewTradePayable(a) : Math.max(0, orderTotal - a);
  // Giỏ có hàng nhưng voucher/điểm đã ăn hết tiền hàng: không còn gì để chiết khấu.
  const capExhausted = tradeCap !== undefined && tradeCap <= 0 && orderTotal > 0;
  const displayTotal = payableAfter(0);
  const appliedCap = Math.min(capBase, remaining);
  const maxAmount = Math.max(0, Math.min(capBase, remaining || capBase));

  useEffect(() => {
    if (!open) return;
    setAmount(initialAmount > 0 ? initialAmount : null);
  }, [open, initialAmount]);

  const computedAmount = useMemo(() => {
    const a = Number(amount || 0);
    if (!Number.isFinite(a) || a <= 0) return 0;
    return Math.min(capBase, remaining, Math.round(a));
  }, [amount, capBase, remaining]);

  function handleOk() {
    if (capExhausted) {
      message.warning(CAP_EXHAUSTED_MESSAGE);
      return;
    }
    if (capBase <= 0) {
      message.warning('Giỏ hàng trống hoặc tổng tiền bằng 0');
      return;
    }
    if (remaining <= 0) {
      message.warning(
        'Khách không còn CK đã quyết toán để trừ. Hãy quyết toán kỳ trước trước khi trừ vào đơn.',
      );
      return;
    }
    if (computedAmount <= 0) {
      message.warning('Vui lòng nhập số tiền chiết khấu');
      return;
    }
    if (computedAmount > remaining) {
      message.warning(`Chỉ còn ${formatVND(remaining)} CK có thể trừ`);
      return;
    }
    onConfirm({
      amount: computedAmount,
      note: 'Chiết khấu tích luỹ B2B',
    });
  }

  return (
    <Modal
      title="Chiết khấu tích luỹ B2B"
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="clear" onClick={() => onConfirm({ amount: 0, note: '' })}>
          Bỏ chiết khấu
        </Button>,
        <Button key="cancel" onClick={onClose}>
          Đóng
        </Button>,
        <Button key="ok" type="primary" onClick={handleOk}>
          Áp dụng
        </Button>,
      ]}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Text type="secondary">
          Trừ thẳng vào tổng tiền đơn. Tổng hiện tại:{' '}
          <Text strong>{formatVND(displayTotal)}</Text>
        </Text>

        <Spin spinning={isFetching}>
          <div
            style={{
              padding: '10px 12px',
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 6,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>Đã quyết toán:</Text>
              <Text strong style={{ color: '#389e0d' }}>{formatVND(settled)}</Text>
            </div>
            {used > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Đã trừ (đơn hoàn thành):</Text>
                <Text strong style={{ color: '#cf1322' }}>-{formatVND(used)}</Text>
              </div>
            )}
            {otherReserved > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>{orderId ? 'Đang giữ trên đơn mở khác:' : 'Đang giữ trên đơn mở:'}</Text>
                <Text strong style={{ color: '#d48806' }}>-{formatVND(otherReserved)}</Text>
              </div>
            )}
            {currentOrderReserved > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Đang giữ trên đơn này:</Text>
                <Text strong style={{ color: '#1677ff' }}>
                  {formatVND(currentOrderReserved)}
                </Text>
              </div>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                borderTop: used > 0 || reserved > 0 ? '1px dashed #b7eb8f' : undefined,
                paddingTop: used > 0 || reserved > 0 ? 4 : 0,
              }}
            >
              <Text strong>{orderId ? 'Có thể dùng cho đơn này:' : 'Còn dùng được:'}</Text>
              <Text strong style={{ color: remaining > 0 ? '#389e0d' : '#999' }}>
                {formatVND(remaining)}
              </Text>
            </div>
            {remaining > 0 ? (
              <div style={{ marginTop: 4 }}>
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0 }}
                  disabled={appliedCap <= 0}
                  onClick={() => setAmount(appliedCap)}
                >
                  Dùng {formatVND(appliedCap)}
                  {remaining > capBase ? ' (giới hạn theo tổng đơn)' : ''}
                </Button>
              </div>
            ) : (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {customerId
                    ? (settled > 0
                        ? 'Đã dùng hết / đang giữ hết CK đã quyết toán trên các đơn trước.'
                        : 'Chưa có CK đã quyết toán. Tính CK dự kiến → Quyết toán kỳ trước rồi mới trừ vào đơn.')
                    : 'Chưa chọn khách hàng tổ chức.'}
                </Text>
              </div>
            )}
            <Text type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
              Số tiền này sẽ được giữ trên đơn; chỉ trừ thật khi đơn COMPLETED.
            </Text>
          </div>
        </Spin>

        {capExhausted && <Text type="warning">{CAP_EXHAUSTED_MESSAGE}</Text>}

        <Input
          disabled={capExhausted}
          inputMode="numeric"
          value={amount == null ? '' : moneyFormatter(amount)}
          onChange={(event) => {
            const digits = event.target.value.replace(/\D/g, '');
            setAmount(digits ? Math.min(moneyParser(digits), maxAmount) : null);
          }}
          style={{ width: '100%' }}
          addonAfter="₫"
          placeholder="Nhập số tiền chiết khấu"
        />

        <div
          style={{
            padding: '8px 12px',
            background: '#fff8f9',
            border: '1px solid #f0d0d8',
            borderRadius: 6,
          }}
        >
          <Text>
            Số tiền trừ: <Text strong style={{ color: '#a4193d' }}>-{formatVND(computedAmount)}</Text>
          </Text>
          <br />
          <Text type="secondary">
            Còn thanh toán: {formatVND(payableAfter(computedAmount))}
          </Text>
        </div>
      </Space>
    </Modal>
  );
}
